import React, { useState, useEffect } from 'react';
import { TextInput } from '@tcl-boron-prefabs/text-input';
import { isValidEmail } from '../../utils/is-valid-email';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import cn from 'classnames';
import './create-account.scss';
import Loader from '@tcl-boron-prefabs/loader';
import { ChevronLeft, Fail } from '@tcl-boron-icons/icons';
import qs from 'query-string';
import { AttemptAutoProvisioning, CreateAccountRequest } from '../../utils/create-account';
import { ROUTES } from '../../navigation/router';
import { useHistory } from 'react-router-dom';
import withStyles from '@tempus/component-library/utils/withStyles';
import styles from '../../styles';

const INVALID_EMAIL_ERROR = 'INVALID_EMAIL';
const ERROR_REQUESTING_ACCOUNT = 'ERROR_REQUESTING_ACCOUNT';
const ERROR_ATTEMPTING_PROVISIONING = 'ERROR_ATTEMPTING_PROVISIONING';
const EXPIRED_URL = 'EXPIRED_URL';

export const EXPIRED_URL_THROWN_MESSAGE = 'URL is expired';

function CreateAccount({ classes }) {
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attemptedProvisioning, setAttemptedProvisioning] = useState(false);
  const [error, setError] = useState(undefined);

  useEffect(() => {
    async function init() {
      const queryObj = qs.parse(window.location.search);
      if (queryObj?.token) {
        window.history.replaceState(null, '', window?.location?.pathname.replace(/\?token=\/\w+/gm, ''));
        handleToken(queryObj?.token);
      }
    }

    init();
  }, [history]);

  async function handleToken(token) {
    setSubmitting(true);
    try {
      await AttemptAutoProvisioning(token);

      setAttemptedProvisioning(true);
      setSubmitting(false);
      setSubmitted(true);
    } catch (error) {
      if (error.message === EXPIRED_URL_THROWN_MESSAGE) {
        setAttemptedProvisioning(true);
        setSubmitting(false);
        setError(EXPIRED_URL);
        setSubmitted(true);
      } else {
        setAttemptedProvisioning(true);
        setSubmitting(false);
        setError(ERROR_ATTEMPTING_PROVISIONING);
        setSubmitted(true);
      }
    }
  }

  function back() {
    history.push(ROUTES.ROOT);
  }

  function updateEmail(value) {
    setEmail(value.trim());
  }

  async function submit() {
    setSubmitting(true);

    if (!isValidEmail(email)) {
      setError(INVALID_EMAIL_ERROR);
      setSubmitting(false);
      return;
    }

    if (submitted) {
      back();
    } else {
      try {
        await CreateAccountRequest(email);

        setSubmitting(false);
        setSubmitted(!submitted);
        setError(undefined);
      } catch (error) {
        setError(ERROR_REQUESTING_ACCOUNT);
        setSubmitting(false);
      }
    }
  }

  function resetState() {
    setEmail('');
    setSubmitted(false);
    setSubmitting(false);
    setAttemptedProvisioning(false);
    setError(undefined);
  }

  function renderCopy() {
    if (submitting) {
      return <div className="submitting-container" />;
    }

    if (attemptedProvisioning && error === EXPIRED_URL) {
      return (
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            This link has expired.
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            We're sorry, but we couldn't create your Hub account because this link has expired. Click below to get sent
            a new link.
          </p>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            Please visit our separate web form to{' '}
            <a className={cn(typography.link)} href="https://go.tempus.com/access-request">
              request access to Hub.
            </a>
          </p>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            Contact{' '}
            <a className={cn(typography.link)} href="mailto:support@tempus.com">
              support@tempus.com{' '}
            </a>
            or call 800-739-4137 for assistance setting up your account.
          </p>
          <div className="custom-button send-instructions-button" onClick={resetState}>
            Back to request an account
          </div>
        </>
      );
    }

    if (attemptedProvisioning && error === ERROR_ATTEMPTING_PROVISIONING) {
      return (
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            Something went wrong.
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            We’re sorry, but we weren’t able to create your Hub account.
          </p>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            Please visit our separate web form to{' '}
            <a className={cn(typography.link)} href="https://go.tempus.com/access-request">
              request access to Hub.
            </a>
          </p>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            Contact{' '}
            <a className={cn(typography.link)} href="mailto:support@tempus.com">
              support@tempus.com{' '}
            </a>
            or call 800-739-4137 for assistance setting up your account.
          </p>
        </>
      );
    }

    if (attemptedProvisioning) {
      return (
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            You now have access to Hub!
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            We’ve successfully added your account. You’re now able to experience all the benefits of Hub.
          </p>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            <strong>Check your inbox for an account activation email.</strong> It could take up to 15 minutes to arrive.
          </p>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            If you're having trouble, contact{' '}
            <a className={cn(typography.link)} href="mailto:support@tempus.com">
              support@tempus.com{' '}
            </a>
            for assistance accessing your account.
          </p>
        </>
      );
    }

    if (submitted && !error) {
      return (
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            Check your inbox
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            An email has been sent to <strong>{email}</strong>.
          </p>
          <p className={cn(typography.Paragraph)}>
            If you do not receive an email in 10 minutes, please check your spam folder, then contact{' '}
            <a className={cn(typography.link)} href="mailto:support@tempus.com">
              support@tempus.com{' '}
            </a>
            for assistance accessing your account.
          </p>
        </>
      );
    }
    return (
      <>
        <h1 className={cn(typography.H1)}>Get access to Tempus Hub!</h1>
        <p className={cn(typography.Paragraph)}>
          Enter the email below you want to use for your new Hub account, and we will send you an email to activate your
          account.
        </p>
      </>
    );
  }

  function getErrorCopy() {
    if (error === INVALID_EMAIL_ERROR) {
      return (
        <div style={{ display: 'flex' }}>
          <Fail height="14px" width="14px" style={{ paddingRight: '2px' }} />
          Please enter a valid email
        </div>
      );
    } else if (error === ERROR_REQUESTING_ACCOUNT) {
      return (
        <div style={{ display: 'flex' }}>
          <Fail height="14px" width="14px" style={{ paddingRight: '2px' }} />
          Something went wrong
        </div>
      );
    }
  }

  const showButton =
    (submitted && error !== EXPIRED_URL) || (!submitted && error === INVALID_EMAIL_ERROR) || (!submitting && !error);
  const shouldShowEmailInput = !submitted && !submitting && !attemptedProvisioning;
  const shouldShowSubmitButton = showButton && !submitting;
  const showBackButton = !submitted && !submitting;

  return (
    <div className="app-view-container create-account-request-message" data-testid="create-account-request-form">
      {submitting ? (
        <div className={cn('app-view-container', classes.loadingContainer)}>
          <Loader />
        </div>
      ) : (
        renderCopy()
      )}
      {shouldShowEmailInput ? (
        <TextInput
          status={error ? 'error' : 'default'}
          placeholder={'email@domain.com'}
          value={email}
          onChange={updateEmail}
          clearable
          subContent={getErrorCopy()}
        />
      ) : null}
      {shouldShowSubmitButton && (
        <div className="custom-button send-instructions-button" onClick={attemptedProvisioning ? back : submit}>
          {submitted ? 'Back to sign in' : 'Send registration email'}
        </div>
      )}
      {showBackButton ? (
        <div className="back-to-sign-in" onClick={back}>
          <ChevronLeft alt="Back to sign in" />
          <span>Back to sign in</span>
        </div>
      ) : null}
    </div>
  );
}

export default withStyles(styles)(CreateAccount);
